/**
 * Computer equipment and backup resources - option lists shared by the two
 * Philippines application forms (agent / Client Care in intl-application.ts
 * and Marketing in marketing-application.ts). Client-safe: no DB access.
 *
 * Source: HR's "TELI Application Form Updates - Computer equipment and backup
 * resources" (October 22, 2026). The texts here are STORED VALUES (reports,
 * the HR view and the conditions read them), so they never change through
 * the Job Applications editor; only the question labels do.
 */

/** "Desktop" / "Laptop" are the values the Marketing form already stored. */
export const COMPUTER_TYPE_OPTIONS = ['Desktop', 'Laptop', 'Both'] as const;
export const COMPUTER_TYPE_LABELS: Record<string, string> = {
  Desktop: 'Desktop computer',
  Laptop: 'Laptop',
  Both: 'Both',
};

/** Which of the two computers is the primary device when "Both" is selected. */
export const DEVICE_OPTIONS = ['Desktop', 'Laptop'] as const;
export const DEVICE_LABELS: Record<string, string> = {
  Desktop: 'Desktop computer',
  Laptop: 'Laptop',
};

export const SPEC_UNKNOWN = 'I do not know';

export const STORAGE_TYPE_OPTIONS = ['SSD', 'HDD', 'Other', SPEC_UNKNOWN] as const;

export const YES_NO_OPTIONS = ['Yes', 'No'] as const;
export const YES_NO_UNKNOWN_OPTIONS = ['Yes', 'No', SPEC_UNKNOWN] as const;

// ---------------------------------------------------------------------------
// Backup power
// ---------------------------------------------------------------------------

export const BACKUP_POWER_NONE = 'I do not have a backup power source';
export const BACKUP_POWER_OTHER = 'Other system';

/**
 * One entry per backup power source the applicant can tick. Each selected
 * source asks for the equipment it keeps running and its estimated runtime
 * (fields `backup_power_<key>_equipment` / `backup_power_<key>_runtime`), so
 * one or several sources are collected the same way.
 */
export const BACKUP_POWER_SOURCES = [
  { option: 'Electric generator', key: 'generator', short: 'Electric generator' },
  { option: 'UPS or portable battery power station', key: 'ups', short: 'UPS or battery power station' },
  { option: 'Solar power system with battery storage', key: 'solar', short: 'Solar power system' },
  { option: BACKUP_POWER_OTHER, key: 'other', short: 'Other system' },
] as const;

export const BACKUP_POWER_OPTIONS = [
  ...BACKUP_POWER_SOURCES.map((s) => s.option),
  BACKUP_POWER_NONE,
] as const;

export const BACKUP_POWER_EQUIPMENT_OPTIONS = [
  'Computer only',
  'Modem/router only',
  'Both computer and modem/router',
] as const;

export const backupPowerEquipmentField = (key: string) => `backup_power_${key}_equipment`;
export const backupPowerRuntimeField = (key: string) => `backup_power_${key}_runtime`;

// ---------------------------------------------------------------------------
// Backup internet
// ---------------------------------------------------------------------------

export const BACKUP_INTERNET_NONE = 'I do not have backup internet';
export const BACKUP_INTERNET_OTHER = 'Other option';

export const BACKUP_INTERNET_OPTIONS = [
  'A second internet connection',
  'Mobile data through a phone hotspot',
  'A portable internet device or pocket Wi-Fi',
  BACKUP_INTERNET_OTHER,
  BACKUP_INTERNET_NONE,
] as const;

// ---------------------------------------------------------------------------
// Durations (battery life, backup runtime): stored as whole minutes
// ---------------------------------------------------------------------------

export const DURATION_MAX_MINUTES = 100000;

/** "2 h 30 min" from stored minutes; '' when not a number. */
export function formatDurationMinutes(value: unknown): string {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(n) || n < 0) return '';
  const minutes = Math.round(n);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}
